import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { PCT, isNoBaselineMetric, isTimeMetric } from './format.js';

// Re-exported for existing importers (SliceTile, tests) that pull these from
// this module. The canonical definitions live in ./format.ts.
export { isNoBaselineMetric, isTimeMetric };

export interface Bucket {
  name: string;
  current: number;
  baseline: number | null;
  n: number;
}

export function toBarRows(buckets: Bucket[]): Bucket[] {
  return buckets.map((b) => ({ ...b }));
}

export interface SliceBarChartProps {
  metric: string;
  buckets: Bucket[];
  valueFormatter?: (v: number) => string;
}

export function SliceBarChart({ metric, buckets, valueFormatter = PCT }: SliceBarChartProps) {
  const data = toBarRows(buckets);
  const showBaseline = !isNoBaselineMetric(metric);
  // Rate metrics are 0..1; pin the axis so an all-zero chart still shows the
  // 0-100% scale (recharts otherwise auto-scales to a meaningless range like
  // 0..400% when every bar is 0). Time metrics use seconds and auto-scale.
  const yDomain: [number, number] | ['auto', 'auto'] = isTimeMetric(metric) ? ['auto', 'auto'] : [0, 1];

  return (
    <ResponsiveContainer width="100%" height={180}>
      <BarChart data={data} margin={{ top: 6, right: 8, bottom: 18, left: 4 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
        <XAxis dataKey="name" fontSize={11} stroke="#6b7280" />
        <YAxis tickFormatter={valueFormatter} fontSize={11} stroke="#6b7280" domain={yDomain} />
        <Tooltip
          formatter={(value: number, _name, entry) => {
            const n = (entry?.payload as Bucket)?.n;
            return [`${valueFormatter(value)} (n=${n ?? 0})`, _name];
          }}
        />
        {showBaseline && <Legend wrapperStyle={{ fontSize: 11 }} />}
        {showBaseline && <Bar dataKey="baseline" fill="#94a3b8" name="baseline" />}
        <Bar dataKey="current" fill="#2563eb" name={showBaseline ? 'current' : undefined}>
          {data.map((d, i) => (
            <Cell key={i} fillOpacity={d.n < 5 ? 0.4 : 1} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
